import React, { useEffect, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import Box from '@rsos/base-components/Box';
import Portal from '@rsos/base-components/Portal';
import { withTheme } from '@rsos/theme/styled';
import {
  BACKDROP_STYLE,
  CONTAINER_STYLE,
  BODY_WRAPPER_STYLE,
} from './Modal.styles';
import {
  ModalDialog,
  ModalDialogInner,
} from './ModalComponents/ModalDialog.styles';

/**
 * Modal component.
 * @param {Boolean} isOpened - Is the modal open?
 * @param {Boolean} transparent - Should the modal have a transparent background?
 * @param {Function} onClose - Callback function for closing the modal.
 * @param {Number} portalPriority - Modal priority order of portal positioning in the DOM.
 * @param {Boolean} disableBackdropClick - Disable click event on modal backdrop.
 * @param {Node} children - Modal content.
 * @param {String} width - Modal width.
 * @param {String} padding - Modal padding around the content.
 * @param {string} id - Modal id.
 * @param {Object} theme - Theme object.
 * @param {String} overflowY - Modal content overflow-y value, determines how the content overflows
 *   the children's top and bottom edges.
 * @param {String} minHeight - Modal min-height.
 * @param {String} maxHeight - Modal max-height.
 */
function Modal({
  isOpened,
  transparent,
  onClose,
  portalPriority,
  disableBackdropClick,
  children,
  width,
  padding,
  id,
  theme,
  overflowY = 'inherit',
  minHeight = '',
  maxHeight = '',
  fitContent = false,
}) {
  const [isInDOM, setIsInDOM] = useState(false);
  useEffect(() => {
    if (!isInDOM && isOpened) {
      setIsInDOM(true);
    }
  }, [isOpened, isInDOM]);

  const handleClickOnBackdrop = () => {
    if (!disableBackdropClick && onClose) {
      onClose();
    }
  };

  const stopPropagation = e => e.stopPropagation();

  const onDisappear = () => {
    setIsInDOM(false);
  };
  if (!isInDOM) return null;

  const modal = (
    <AnimatedModal
      isOpened={isOpened}
      onDisappear={onDisappear}
      onBackdropClick={handleClickOnBackdrop}
      theme={theme}
    >
      <ModalDialog
        width={width}
        minHeight={minHeight}
        maxHeight={maxHeight}
        data-name={id ? `${id}-dialog` : undefined}
      >
        <ModalDialogInner
          onClick={stopPropagation}
          transparent={transparent}
          padding={padding}
          theme={theme}
          fitContent={fitContent}
        >
          <Box grow overflowY={overflowY}>
            {children}
          </Box>
        </ModalDialogInner>
      </ModalDialog>
    </AnimatedModal>
  );

  return (
    <Portal id={id} priority={portalPriority}>
      {modal}
    </Portal>
  );
}

const AnimatedModal = ({
  isOpened,
  onDisappear,
  onBackdropClick,
  children,
  theme,
}) => {
  const [isFirstRender, setFirstRender] = useState(true);

  const isVisible = isOpened && !isFirstRender;

  useEffect(() => {
    setFirstRender(false);
  }, []);

  const backdropAnimation = useSpring({
    opacity: isVisible ? 0.3 : 0,
    config: {
      tension: 350,
    },
  });

  const backdropStyle = {
    ...BACKDROP_STYLE,
    ...{ background: theme.colors.modal.scrim },
    ...backdropAnimation,
  };
  const containerStyle = {
    ...CONTAINER_STYLE,
    pointerEvents: isVisible ? 'auto' : 'none',
  };

  const bodyWrapperAnimation = useSpring({
    opacity: isVisible ? 1 : 0,
    transform: `scale(${isVisible ? 1 : 0.96})`,
    config: {
      tension: 450,
      friction: 35,
    },
    onRest: () => {
      if (!isVisible) onDisappear();
    },
  });

  const bodyWrapperStyle = {
    ...BODY_WRAPPER_STYLE,
    ...bodyWrapperAnimation,
  };

  return (
    <>
      <animated.div style={backdropStyle} />
      <div style={containerStyle} onClick={onBackdropClick}>
        <animated.div style={bodyWrapperStyle}>{children}</animated.div>
      </div>
    </>
  );
};

export default withTheme(Modal);
